import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import React, {useEffect, useState} from "react";
import groupService from "../../../../services/web/group-service.js";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../utils/project-utils.js";
import {Template} from "../../../../components/template/template.jsx";
import {FaInfoCircle, FaPlus} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import Button from "../../../../components/button/button.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import Alert from "../../../../components/alert/alert.jsx";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SentryRow from "../../../../components/template/sentry-row.jsx";

const initialState = {
    name: "",
    notification: false,
    occurrence: false,
    association_network: false,
    ecap: false,
}

const GroupForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const [state, setState] = useState(initialState)
    const [isLoading, setIsLoading] = useState(false)

    const {data, refetch} = useQuery({
        queryFn: () => groupService.get(id),
        queryKey: ['group'],
        enabled: !!id,
        staleTime: 0
    });

    useEffect(() => {
        if (!!id && data) {
            setState({
                name: data.name,
                notification: data.notification,
                occurrence: data.occurrence,
                association_network: data.association_network,
                ecap: data.ecap
            });
        } else  {
            setState(initialState);
        }
    }, [id, data]);

    const submit = () => {
        if (id === 1) return;

        setIsLoading(true)

        const formData = new FormData();
        formData.append("name", state.name);
        formData.append("occurrence", state.occurrence);
        formData.append("notification", state.notification);
        formData.append("association_network", state.association_network);
        formData.append("ecap", state.ecap);

        let response;
        if (id) {
            formData.append("_method", "PUT")
            response = groupService.edit(id, formData);
        }
        else response = groupService.create(formData);

        response
            .then(({data}) => {
                projectUtils.success(!id);
                
                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/group/form/${data.id}`;
                navigate(urlDev);
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                setIsLoading(false)
                projectUtils.swalErrorMessage('', message, 'Ok');
            }).finally(() => setIsLoading(false));
    }

    const deleteGroup = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir o grupo de operação "${state.name ?? id}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    groupService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/group");
                        })
                        .catch(error => {
                            console.error(error);
                            let message = 'Não foi possível excluir.';
                            if (error.response.status !== 500) {
                                message = error.response.data.message;
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok');
                        });
                }
            }
        );
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Grupo de operação</Template.Title>

                {
                    id &&
                    <Button target={"default"} onClick={() => navigate("/group/form")}>
                        <FaPlus /> Adicionar grupo
                    </Button>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <InputForm
                                id={"name"}
                                classNameWrapper={"col-span-6"}
                                label={"Nome *"}
                                value={state.name}
                                onChange={({target: {value}}) => setState(prev => ({...prev, name: value}))}
                            />

                            <section className={"col-span-6 flex gap-2 items-center"}>
                                <p>Módulos:</p>
                                <InputCheckbox
                                    id="occurrence"
                                    label="Ocorrência"
                                    checked={state.occurrence}
                                    disabled={id === "1"}
                                    onChange={({target: {checked}}) => setState(prev => ({...prev, occurrence: checked}))}
                                />

                                <InputCheckbox
                                    id="notification"
                                    label="Notificações"
                                    checked={state.notification}
                                    disabled={id === "1"}
                                    onChange={({target: {checked}}) => setState(prev => ({...prev, notification: checked}))}
                                />

                                <InputCheckbox
                                    id="association-network"
                                    label="Rede de associação"
                                    checked={state.association_network}
                                    disabled={id === "1"}
                                    onChange={({target: {checked}}) => setState(prev => ({...prev, association_network: checked}))}
                                />

                                <InputCheckbox
                                    id="ecap"
                                    label="ECAP"
                                    checked={state.ecap}
                                    disabled={id === "1"}
                                    onChange={({target: {checked}}) => setState(prev => ({...prev, ecap: checked}))}
                                />
                            </section>
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className={"flex gap-2 items-center " + (id === 1 ? "justify-start" : "justify-end")}>
                        {
                            id === "1" &&
                            <Alert className={"flex gap-2 items-center"} target={"info"}>
                                <FaInfoCircle /> Esse é um grupo de operação padrão de sistema e não pode ser editado ou excluído
                            </Alert>
                        }

                        {
                            id !== "1" &&
                            <>
                                {
                                    id &&
                                    <>
                                        {
                                            data && !data?.deletable &&
                                            <Alert className={"flex gap-2 items-center"} target={"warning"}>
                                                Não é possível excluir, pois existem associações dependentes do grupo de operação.
                                            </Alert>
                                        }

                                        <Button target={"danger"} onClick={deleteGroup} disabled={!data?.deletable} actionFeedbackType='delete'>

                                            Excluir
                                        </Button>
                                    </>
                                }
                                <Button onClick={submit} actionFeedbackType='save'>

                                    Salvar
                                </Button>
                            </>
                        }
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default GroupForm;